import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { fetchInstallerCollectoren, patchCollectorOpDruk, uploadDrukattestPhoto, listDrukattestPhotos, deleteDrukattestPhoto } from '@/api/installer';
import { listCollectorPhotos, uploadCollectorPhoto, deleteCollectorPhoto, fetchProject, listProjectPlans } from '@/api/tasks';
import { usePermissionsStore } from '@/stores/permissions';
import { MeerwerkenSection } from '@/components/project/MeerwerkenSection';
import { InstallerKringenTable } from '@/components/installer/InstallerKringenTable';
import { compressImage } from '@/utils/imageCompression';
import { uploadPool } from '@/utils/uploadPool';
import { buildBlokGroups, getCollectorFotoLabel, getCollectorMeta } from '@/utils/collectors';
import type { BlokGroup, VerdiepingGroup } from '@/utils/collectors';
import type { Collector, PhotoItem, ProjectDetail, ProjectTask } from '@/types/odoo';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { PlanFileList } from '@/components/ui/PlanFileList';
import { isPdfPhoto } from '@/utils/photoFiles';
import { SafeHtml } from '@/components/ui/SafeHtml';
import { descriptionHasContent } from '@/utils/descriptionSanitize';
import { describeUploadFailures } from '@/utils/uploadFeedback';
import { InterventieTaskPhotoSection } from '@/components/interventie/InterventieTaskPhotoSection';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';

function findNearestFutureTask(tasks: ProjectTask[]): number | null {
  const pad = (n: number) => String(n).padStart(2, '0');
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const future = tasks
    .filter(t => t.date_deadline && String(t.date_deadline).slice(0, 10) >= todayStr)
    .sort((a, b) => String(a.date_deadline).localeCompare(String(b.date_deadline)));
  return future[0]?.id ?? tasks[0]?.id ?? null;
}


export function InstallerProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectName: routeProjectName, werfLocation, checkinAtWork, from } = (location.state as { projectName?: string; werfLocation?: string; checkinAtWork?: string; from?: string }) || {};
  // Back goes where the crew came from. Mijn Werk taps through to a task too, and
  // landing them on the planning afterwards loses their place in a long PO.
  const backTo = from ?? '/installer';
  const urlTaskId = searchParams.get('task') ? Number(searchParams.get('task')) : null;
  const pid = Number(projectId);

  const [collectors, setCollectors] = useState<Collector[]>([]);
  const [installerNames, setInstallerNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const canFeature = usePermissionsStore((s) => s.canFeature);
  const canToggle = canFeature('collector_toggle');

  // Extra data for info/descriptions/plans sections
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [plans, setPlans] = useState<PhotoItem[]>([]);

  useEffect(() => {
    if (!pid) return;
    setLoading(true);
    setError(null);
    fetchInstallerCollectoren(pid)
      .then((data) => {
        setCollectors(data.collectors);
        setInstallerNames(data.installerNames);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Collectoren ophalen mislukt');
        setLoading(false);
      });

    // Fetch project detail for info + descriptions (best effort)
    fetchProject(pid)
      .then((detail) => setProjectDetail(detail))
      .catch(() => {}); // silently fail — info section just won't show
  }, [pid]);

  // Fetch plans
  useEffect(() => {
    if (!pid) return;
    listProjectPlans(pid)
      .then((data) => setPlans(data.plans))
      .catch(() => {}); // silently fail
  }, [pid]);

  // Auto-select nearest future task if no ?task= in URL
  const [routeTaskId, setRouteTaskId] = useState<number | undefined>(urlTaskId ?? undefined);
  useEffect(() => {
    if (urlTaskId) {
      setRouteTaskId(urlTaskId);
    } else if (projectDetail) {
      const nearest = findNearestFutureTask(projectDetail.projectTasks);
      if (nearest) {
        setRouteTaskId(nearest);
        setSearchParams({ task: String(nearest) }, { replace: true });
      }
    }
  }, [urlTaskId, projectDetail, setSearchParams]);

  const handleOpDrukToggle = useCallback(async (collector: Collector) => {
    const newValue = !collector.x_studio_collector_op_druk;
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const today = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;

    // Optimistic update
    setCollectors((prev) =>
      prev.map((c) =>
        c.id === collector.id
          ? { ...c, x_studio_collector_op_druk: newValue, x_studio_collector_op_druk_datum: newValue ? today : false }
          : c,
      ),
    );

    try {
      await patchCollectorOpDruk(pid, collector.id, newValue);
    } catch {
      // Revert on error
      setCollectors((prev) =>
        prev.map((c) =>
          c.id === collector.id
            ? { ...c, x_studio_collector_op_druk: collector.x_studio_collector_op_druk, x_studio_collector_op_druk_datum: collector.x_studio_collector_op_druk_datum }
            : c,
        ),
      );
    }
  }, [pid]);

  const handleOpDrukAutoSet = useCallback((collectorId: number, value: boolean) => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const today = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
    setCollectors((prev) =>
      prev.map((c) =>
        c.id === collectorId
          ? {
              ...c,
              x_studio_collector_op_druk: value,
              x_studio_collector_op_druk_datum: value ? today : false,
            }
          : c,
      ),
    );
  }, []);

  const handleToggleExpand = useCallback((id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  if (loading) {
    return (
      <div className="px-4 py-5 max-w-lg lg:max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
          <div className="flex-1">
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-1 animate-pulse" />
            <div className="h-4 bg-gray-100 rounded w-1/2 animate-pulse" />
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-5 max-w-lg lg:max-w-4xl mx-auto">
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="flex items-center gap-2 text-td-blue text-sm font-medium mb-4 min-h-[48px]"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Terug
        </button>
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Fout bij laden</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const blokGroups = buildBlokGroups(collectors);

  // The active planning task: its date_deadline feeds op_druk_datum, its Interventie tag the photo section
  const activeTask = projectDetail?.projectTasks.find(t => t.id === routeTaskId);
  const taskDateDeadline = activeTask?.date_deadline ? String(activeTask.date_deadline).slice(0, 10) : undefined;

  return (
    <div className="px-4 py-5 max-w-lg lg:max-w-4xl mx-auto">
      {/* Header with back button */}
      <div className="flex items-start gap-3 mb-5">
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          aria-label={backTo === '/installer/werk' ? 'Terug naar mijn werk' : 'Terug naar taken'}
        >
          <svg className="w-5 h-5 text-td-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="min-w-0">
          <h1 className="text-lg font-extrabold text-td-navy truncate">
            {routeProjectName || projectDetail?.projectName || `Project ${projectId}`}{activeTask?.isInterventie && <InterventieBadge className="ml-2 align-middle" />}
          </h1>
          {werfLocation && (
            <p className="text-xs text-td-muted mt-0.5 truncate">{werfLocation}</p>
          )}
          {checkinAtWork && (
            <p className="text-xs text-td-blue font-medium mt-0.5 truncate">Checkin@work: {checkinAtWork}</p>
          )}
          {!(activeTask?.isInterventie && collectors.length === 0) && <p className="text-xs text-td-muted mt-0.5">
            {collectors.length} {collectors.length === 1 ? 'collector' : 'collectoren'} in {blokGroups.length} {blokGroups.length === 1 ? 'blok' : 'blokken'}
          </p>}
        </div>
      </div>

      {projectDetail && (
        <div className="lg:grid lg:grid-cols-2 lg:gap-4">
          {/* Project info card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 lg:mb-0">
            <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-2">Projectinfo</h2>
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Project</dt>
                <dd className="text-sm font-bold text-td-navy mt-0.5">{projectDetail.projectName}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">S-nummer</dt>
                <dd className="text-sm font-bold text-td-blue mt-0.5">{projectDetail.saleOrder?.name ?? '-'}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Klant</dt>
                <dd className="text-sm font-bold text-td-navy mt-0.5">{projectDetail.saleOrder?.partner_id ? projectDetail.saleOrder.partner_id[1] : '-'}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Adres</dt>
                <dd className="text-sm font-bold text-td-navy mt-0.5">{projectDetail.werfLocation || werfLocation || '-'}</dd>
              </div>
              {projectDetail.projectleiderName && (
                <div>
                  <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Projectleider</dt>
                  <dd className="text-sm font-bold text-td-navy mt-0.5">{projectDetail.projectleiderName}</dd>
                </div>
              )}
              {checkinAtWork && (
                <div>
                  <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Checkin@work</dt>
                  <dd className="text-sm font-bold text-td-blue mt-0.5">{checkinAtWork}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Task descriptions */}
          <InstallerTaskDescriptions projectDetail={projectDetail} activeTaskId={routeTaskId ?? null} />
        </div>
      )}

      {/* Installer team */}
      {projectDetail && (
        <InstallerTeamSection projectDetail={projectDetail} activeTaskId={routeTaskId ?? null} />
      )}

      {/* Meerwerken */}
      {projectDetail && canFeature('meerwerk_log') && (
        <MeerwerkenSection projectId={projectDetail.projectId} taskId={activeTask?.isInterventie && Array.isArray(activeTask.parent_id) ? activeTask.parent_id[0] : routeTaskId ?? null} taskDate={taskDateDeadline ?? null} />
      )}

      {/* A repair: its photos sit below Meerwerken / Notes (or where that card would be), after the project info */}
      {activeTask?.isInterventie && <InterventieTaskPhotoSection taskId={activeTask.id} canUpload={canFeature('photo_upload')} canDelete={canFeature('photo_upload')} urenMelden={canFeature('meerwerk_log')} />}
      {/* Floor plans */}
      {plans.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-3">Verlegschema's</h2>
          <div className="space-y-2">
            <PlanFileList
              plans={plans}
              linkLabel="Downloaden"
              linkClassName="shrink-0 px-3 py-1.5 text-xs font-bold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors min-h-[36px] flex items-center"
            />
          </div>
        </div>
      )}

      {collectors.length === 0 && !activeTask?.isInterventie && (
        <div className="text-center py-12">
          <p className="text-td-muted text-sm">Geen collectoren voor jou op deze werf</p>
          <p className="text-td-muted text-xs mt-1">
            Je ziet enkel je eigen collectoren en collectoren die nog vrij zijn.
          </p>
        </div>
      )}

      {/* Blok groups — only render once projectName is resolved, otherwise uploads would land under a wrong path */}
      {(() => {
        const resolvedProjectName = projectDetail?.projectName || routeProjectName || '';
        if (!resolvedProjectName) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-700">
                Projectnaam wordt geladen. Als dit blijft hangen, ga terug en open het project opnieuw.
              </p>
            </div>
          );
        }
        return (
          <div className="space-y-3">
            {blokGroups.map((group, index) => (
              <InstallerBlokSection
                key={group.blok}
                group={group}
                defaultOpen={index === 0}
                installerNames={installerNames}
                projectId={pid}
                projectName={resolvedProjectName}
                expandedId={expandedId}
            onToggleExpand={handleToggleExpand}
            onOpDrukToggle={handleOpDrukToggle}
            canToggle={canToggle}
            onFotoStatusChange={(collectorId, status) => setCollectors((prev) =>
              prev.map((c) => c.id === collectorId ? { ...c, x_studio_fotos_uitvoering: status } : c)
            )}
                onOpDrukAutoSet={(collectorId, value) => handleOpDrukAutoSet(collectorId, value)}
                taskDateDeadline={taskDateDeadline}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

/* ─── Task Descriptions (single task + toggle) ─── */

function InstallerTaskDescriptions({
  projectDetail,
  activeTaskId,
}: {
  projectDetail: ProjectDetail;
  activeTaskId: number | null;
}) {
  const tasksWithDesc = projectDetail.projectTasks.filter((t) => descriptionHasContent(t.description));

  // Arriving with a task in the URL — tapping a collector in Mijn werk does this —
  // means asking about THAT task. A collector task carries no description of its own,
  // and silently answering with another task's is how an aannemer came to read the
  // customer's prices off the close-out task under the heading "Taakomschrijving".
  if (activeTaskId) {
    const clicked = projectDetail.projectTasks.find((t) => t.id === activeTaskId);
    if (clicked && !descriptionHasContent(clicked.description)) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 lg:mb-0">
          <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-3">Taakomschrijving</h2>
          <p className="text-sm font-semibold text-td-navy">{clicked.name}</p>
          <p className="text-sm text-td-muted italic mt-1">Geen omschrijving voor deze taak</p>
        </div>
      );
    }
  }

  if (tasksWithDesc.length === 0) return null;

  // Primary task: match clicked task, or nearest deadline, or first
  let primaryTask = tasksWithDesc[0];
  if (activeTaskId) {
    const match = tasksWithDesc.find((t) => t.id === activeTaskId);
    if (match) primaryTask = match;
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const future = tasksWithDesc
      .filter((t) => t.date_deadline && String(t.date_deadline).split(' ')[0] >= todayStr)
      .sort((a, b) => String(a.date_deadline).localeCompare(String(b.date_deadline)));
    if (future.length > 0) primaryTask = future[0];
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 lg:mb-0">
      <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-3">Taakomschrijving</h2>
      <div>
        <p className="text-sm font-semibold text-td-navy">{primaryTask.name}</p>
        {primaryTask.parent_id && (
          <p className="text-[10px] font-bold text-td-muted mt-0.5">{primaryTask.parent_id[1]}</p>
        )}
        <SafeHtml html={primaryTask.description || ''} className="text-sm text-td-navy mt-1.5" />
      </div>
    </div>
  );
}

/* ─── Installer Team Section ─── */

function InstallerTeamSection({
  projectDetail,
  activeTaskId,
}: {
  projectDetail: ProjectDetail;
  activeTaskId: number | null;
}) {
  const activeTask = projectDetail.projectTasks.find((t) => t.id === activeTaskId);
  if (!activeTask) return null;

  const installerIds = activeTask.x_studio_installateur;
  if (!installerIds || installerIds.length === 0) return null;

  const names = installerIds.map(
    (id) => projectDetail.installerNames[String(id)] ?? 'Onbekend',
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
      <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide mb-2">Installateurs</h2>
      <div className="space-y-1">
        {names.map((name, i) => (
          <p key={i} className="text-sm font-semibold text-td-navy">{name}</p>
        ))}
      </div>
    </div>
  );
}


/* ─── Drukattest Photo Section ─── */

function DrukattestPhotoSection({
  projectId,
  projectName,
  blokName,
}: {
  projectId: number;
  projectName: string;
  blokName: string;
}) {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listDrukattestPhotos(projectId, projectName, blokName);
      setPhotos(items);
    } catch {
      // Silently fail, photos just won't show
    } finally {
      setLoading(false);
    }
  }, [projectId, projectName, blokName]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleUpload = async (files: FileList) => {
    const fileArr = Array.from(files);
    setUploading(true);
    setUploadError(null);
    setUploadProgress({ done: 0, total: fileArr.length });

    const { failed, failures } = await uploadPool(fileArr, async (file) => {
      const compressed = await compressImage(file);
      const result = await uploadDrukattestPhoto(projectId, compressed, projectName, blokName);
      setPhotos((prev) => [...prev, {
        key: result.key,
        url: '',
        filename: file.name,
        lastModified: new Date().toISOString(),
        size: compressed.size,
      }]);
      setUploadProgress((prev) => prev ? { ...prev, done: prev.done + 1 } : null);
    }, 2);

    await loadPhotos();
    setUploading(false);
    setUploadProgress(null);

    if (failed > 0) {
      setUploadError(describeUploadFailures(failed, failures));
    }
  };

  const handleDelete = async (key: string) => {
    setDeleting(key);
    try {
      await deleteDrukattestPhoto(projectId, key);
      setPhotos((prev) => prev.filter((p) => p.key !== key));
    } catch {
      // silently fail
    } finally {
      setDeleting(null);
    }
  };

  // PhotoLightbox is an image viewer; a PDF opens in a new tab instead.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));

  return (
    <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-white border border-gray-200">
      <p className="text-[10px] font-bold text-td-muted uppercase tracking-wide shrink-0">Drukattest</p>

      {loading && (
        <div className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
          <span className="sr-only">Laden...</span>
        </div>
      )}

      {!loading && (
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {photos.map((p) => (
            <div key={p.key} className="relative shrink-0 group">
              <PhotoGridThumb
                photo={p}
                variant="strip"
                imgClassName={`w-10 h-10 object-cover rounded border border-gray-200 hover:border-td-blue transition-colors ${deleting === p.key ? 'opacity-40' : ''}`}
                openLabel="Drukattest foto openen"
                onOpen={() => { setLightboxIndex(imagePhotos.findIndex((i) => i.key === p.key)); setLightboxOpen(true); }}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleDelete(p.key); }}
                disabled={deleting === p.key}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                aria-label={`Foto verwijderen`}
              >
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="shrink-0 w-10 h-10 border-2 border-dashed border-gray-300 rounded flex items-center justify-center hover:border-td-blue transition-colors disabled:opacity-50"
            aria-label="Drukattest foto uploaden"
          >
            {uploading ? (
              <div className="w-3.5 h-3.5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
                <span className="sr-only">Uploaden...</span>
              </div>
            ) : (
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
              </svg>
            )}
          </button>
        </div>
      )}

      {uploading && uploadProgress && (
        <span className="text-[10px] text-td-muted shrink-0">
          {uploadProgress.done}/{uploadProgress.total} geüpload...
        </span>
      )}

      {!uploading && photos.length > 0 && (
        <span className="text-[10px] font-bold text-td-muted shrink-0">{photos.length}</span>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        multiple
        onChange={(e) => e.target.files && handleUpload(e.target.files)}
        className="hidden"
      />

      {uploadError && (
        <p className="text-[10px] text-red-500 shrink-0">{uploadError}</p>
      )}

      {lightboxOpen && imagePhotos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxIndex}
          onClose={() => { setLightboxOpen(false); }}
        />
      )}
    </div>
  );
}

/* ─── Blok Section (deprecated — kept for reference) ─── */

function InstallerBlokSection({
  group,
  defaultOpen,
  installerNames,
  projectId,
  projectName,
  expandedId,
  onToggleExpand,
  onOpDrukToggle,
  canToggle,
  onFotoStatusChange,
  onOpDrukAutoSet,
  taskDateDeadline,
}: {
  group: BlokGroup;
  defaultOpen: boolean;
  installerNames: Record<string, string>;
  projectId: number;
  projectName: string;
  expandedId: number | null;
  onToggleExpand: (id: number) => void;
  onOpDrukToggle: (collector: Collector) => void;
  canToggle: boolean;
  onFotoStatusChange: (collectorId: number, status: string) => void;
  onOpDrukAutoSet: (collectorId: number, value: boolean) => void;
  taskDateDeadline?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left rounded-xl border border-gray-200 bg-white hover:border-td-blue/40 hover:shadow-sm p-3 transition-all min-h-[48px]"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 text-td-muted shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <p className="text-sm font-bold text-td-navy truncate flex-1 min-w-0">
            Blok {group.blok}
          </p>
          <span className="shrink-0 text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded">
            {group.totalCollectors} {group.totalCollectors === 1 ? 'collector' : 'collectoren'}
          </span>
        </div>
      </button>

      {open && (
        <div className="mt-2 ml-4 space-y-1.5">
          {/* Drukattest photos row */}
          <DrukattestPhotoSection
            projectId={projectId}
            projectName={projectName}
            blokName={group.blok}
          />

          {group.verdiepingen.map((vg, index) => (
            <InstallerVerdiepingSection
              key={vg.verdieping}
              group={vg}
              defaultOpen={defaultOpen && index === 0}
              installerNames={installerNames}
              projectId={projectId}
              projectName={projectName}
              expandedId={expandedId}
              onToggleExpand={onToggleExpand}
              onOpDrukToggle={onOpDrukToggle}
              canToggle={canToggle}
              onFotoStatusChange={onFotoStatusChange}
              onOpDrukAutoSet={onOpDrukAutoSet}
              taskDateDeadline={taskDateDeadline}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Verdieping Section ─── */

function InstallerVerdiepingSection({
  group,
  defaultOpen,
  installerNames,
  projectId,
  projectName,
  expandedId,
  onToggleExpand,
  onOpDrukToggle,
  canToggle,
  onFotoStatusChange,
  onOpDrukAutoSet,
  taskDateDeadline,
}: {
  group: VerdiepingGroup;
  defaultOpen: boolean;
  installerNames: Record<string, string>;
  projectId: number;
  projectName: string;
  expandedId: number | null;
  onToggleExpand: (id: number) => void;
  onOpDrukToggle: (collector: Collector) => void;
  canToggle: boolean;
  onFotoStatusChange: (collectorId: number, status: string) => void;
  onOpDrukAutoSet: (collectorId: number, value: boolean) => void;
  taskDateDeadline?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left rounded-lg border border-gray-100 bg-white hover:border-td-blue/30 hover:shadow-sm px-3 py-2 transition-all min-h-[48px]"
      >
        <div className="flex items-center gap-2 min-w-0">
          <svg
            className={`w-3.5 h-3.5 text-td-muted shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-bold text-td-navy truncate">
            Verdieping {group.verdieping}
          </span>
          <span className="shrink-0 text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded ml-auto">
            {group.collectors.length}
          </span>
        </div>
      </button>

      {open && (
        <div className="mt-1.5 ml-4 space-y-1.5">
          {group.collectors.map((collector) => (
            <InstallerCollectorCard
              key={collector.id}
              collector={collector}
              installerNames={installerNames}
              projectId={projectId}
              projectName={projectName}
              isExpanded={expandedId === collector.id}
              onToggleExpand={() => onToggleExpand(collector.id)}
              onOpDrukToggle={() => onOpDrukToggle(collector)}
              canToggle={canToggle}
              onFotoStatusChange={(status) => onFotoStatusChange(collector.id, status)}
              onOpDrukAutoSet={(value) => onOpDrukAutoSet(collector.id, value)}
              taskDateDeadline={taskDateDeadline}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Collector Card ─── */

function InstallerCollectorCard({
  collector,
  installerNames,
  projectId,
  projectName,
  isExpanded,
  onToggleExpand,
  onOpDrukToggle,
  canToggle,
  onFotoStatusChange,
  onOpDrukAutoSet,
  taskDateDeadline,
}: {
  collector: Collector;
  installerNames: Record<string, string>;
  projectId: number;
  projectName: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpDrukToggle: () => void;
  canToggle: boolean;
  onFotoStatusChange: (status: string) => void;
  onOpDrukAutoSet: (value: boolean) => void;
  taskDateDeadline?: string;
}) {
  const fotoLabel = getCollectorFotoLabel(collector);

  return (
    <div className={`bg-white rounded-xl border ${isExpanded ? 'border-td-blue/30 shadow-sm' : 'border-gray-200'} transition-all`}>
      {/* Main row */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full text-left p-3 min-h-[48px]"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-td-navy truncate">{collector.name}</p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              <span className="text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded">
                {collector.x_studio_collector_m2} m²
              </span>
              {collector.x_studio_collector_systeem && (
                <span className="text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded">
                  {collector.x_studio_collector_systeem}
                </span>
              )}
              {fotoLabel && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  fotoLabel === 'Goedgekeurd' ? 'bg-green-100 text-green-700' :
                  fotoLabel === 'Geupload' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-td-muted'
                }`}>
                  {fotoLabel}
                </span>
              )}
            </div>
          </div>

          {/* Op druk status + chevron */}
          <div className="flex items-center gap-2 shrink-0">
            {collector.x_studio_collector_op_druk ? (
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-green-100" aria-label="Op druk">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </span>
            ) : (
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100" aria-label="Niet op druk">
                <span className="w-3 h-3 rounded-full bg-gray-300" />
              </span>
            )}
            <svg
              className={`w-4 h-4 text-gray-300 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </button>

      {/* Expanded section: Op druk toggle + kringen + photos */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-3 space-y-3">
          {/* Op druk toggle slider */}
          <div className="flex items-center justify-between">
            <span className={`text-sm font-semibold ${collector.x_studio_collector_op_druk ? 'text-green-600' : 'text-td-navy'}`}>
              Op druk
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={!!collector.x_studio_collector_op_druk}
              aria-label="Op druk zetten"
              disabled={!canToggle}
              onClick={(e) => {
                e.stopPropagation();
                onOpDrukToggle();
              }}
              className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-td-blue focus:ring-offset-2 ${
                collector.x_studio_collector_op_druk
                  ? 'bg-green-500'
                  : 'bg-gray-300'
              } ${!canToggle || collector.x_studio_collector_op_druk ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  collector.x_studio_collector_op_druk ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Kringen: systeem, legpatroon, lengte en de afgeleide m² per kring */}
          <InstallerKringenTable collector={collector} />

          {/* Photo section */}
          <InstallerPhotoSection
            collector={collector}
            projectName={projectName}
            onFotoStatusChange={onFotoStatusChange}
            onOpDrukAutoSet={onOpDrukAutoSet}
            taskDateDeadline={taskDateDeadline}
          />
        </div>
      )}
    </div>
  );
}

/* ─── Photo Section ─── */

function InstallerPhotoSection({
  collector,
  projectName,
  onFotoStatusChange,
  onOpDrukAutoSet,
  taskDateDeadline,
}: {
  collector: Collector;
  projectName: string;
  onFotoStatusChange: (status: string) => void;
  onOpDrukAutoSet: (value: boolean) => void;
  taskDateDeadline?: string;
}) {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const baseMeta = getCollectorMeta(collector, projectName);
  const meta = taskDateDeadline ? { ...baseMeta, taskDateDeadline } : baseMeta;

  const loadPhotos = useCallback(async () => {
    try {
      const r = await listCollectorPhotos(collector.id, meta);
      setPhotos(r.photos);
    } catch {
      // Silently fail, photos just won't show
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collector.id]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleUpload = async (files: FileList) => {
    const fileArr = Array.from(files);
    setUploading(true);
    setUploadError(null);
    setUploadProgress({ done: 0, total: fileArr.length });

    let anyOpDrukChanged = false;
    const { succeeded, failed, failures } = await uploadPool(fileArr, async (file) => {
      const compressed = await compressImage(file);
      const result = await uploadCollectorPhoto(collector.id, compressed, meta);
      if (result.opDrukChanged) anyOpDrukChanged = true;
      setPhotos((prev) => [...prev, {
        key: result.key,
        url: result.url,
        filename: file.name,
        lastModified: new Date().toISOString(),
        size: compressed.size,
      }]);
      setUploadProgress((prev) => prev ? { ...prev, done: prev.done + 1 } : null);
    }, 2);

    await loadPhotos();
    if (succeeded > 0) {
      onFotoStatusChange('Fotos_Geupload');
    }
    if (anyOpDrukChanged) {
      onOpDrukAutoSet(true);
    }
    setUploading(false);
    setUploadProgress(null);

    if (failed > 0) {
      setUploadError(describeUploadFailures(failed, failures));
    }
  };

  const handleDelete = async (key: string) => {
    setDeleting(key);
    try {
      const result = await deleteCollectorPhoto(collector.id, key);
      const remaining = photos.filter((p) => p.key !== key);
      setPhotos(remaining);
      if (remaining.length === 0) {
        onFotoStatusChange('Geen_Fotos');
      }
      if (result.opDrukChanged) {
        onOpDrukAutoSet(false);
      }
    } catch {
      // silently fail
    } finally {
      setDeleting(null);
    }
  };

  // PhotoLightbox is an image viewer; a PDF opens in a new tab instead.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));

  return (
    <div>
      <p className="text-xs font-bold text-td-muted uppercase tracking-wide mb-2">
        Foto&apos;s
      </p>

      <div className="flex flex-wrap gap-2 items-center">
        {loading && (
          <div className="w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
              <span className="sr-only">Foto's laden...</span>
            </div>
          </div>
        )}

        {!loading && photos.map((p) => (
          <div key={p.key} className="relative shrink-0 group">
            <PhotoGridThumb
              photo={p}
              tileSizeClass="w-20 h-20 lg:w-24 lg:h-24"
              imgClassName={`w-20 h-20 lg:w-24 lg:h-24 object-cover rounded-lg border border-gray-200 hover:border-td-blue transition-colors ${deleting === p.key ? 'opacity-40' : ''}`}
              onOpen={() => { setLightboxIndex(imagePhotos.findIndex((i) => i.key === p.key)); setLightboxOpen(true); }}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDelete(p.key); }}
              disabled={deleting === p.key}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 transition-colors"
              aria-label={`Foto ${p.filename} verwijderen`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}

        {!loading && (
          <div className="flex flex-col items-center gap-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-20 h-20 lg:w-24 lg:h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-td-blue transition-colors disabled:opacity-50"
              aria-label="Foto uploaden"
            >
              {uploading ? (
                <div className="w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
                  <span className="sr-only">Uploaden...</span>
                </div>
              ) : (
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
            {uploading && uploadProgress && (
              <span className="text-[10px] text-td-muted">
                {uploadProgress.done}/{uploadProgress.total} geüpload...
              </span>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
            multiple
          onChange={(e) => e.target.files && handleUpload(e.target.files)}
          className="hidden"
          aria-label="Selecteer foto's om te uploaden"
        />
      </div>

      {uploadError && (
        <p className="text-xs text-red-500 mt-1">{uploadError}</p>
      )}

      {lightboxOpen && imagePhotos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxIndex}
          onClose={() => { setLightboxOpen(false); }}
        />
      )}
    </div>
  );
}
